import { Component, HostListener, ElementRef, ViewChild, inject, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterOutlet, Router, RouterModule } from "@angular/router";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { formatTime } from "../shared/utils/commenFunctions";
import {
  getStaffInfo,
  getSelectedLocation,
  getStaffAssignedLocations,
  setSelectedLocation,
  setStaffAssignedLocations,
} from "../shared/utils/storage-utils";
import { StaffLogoutService } from "../core/services/staff-logout.service";
import { LocationSelectionModalComponent } from "../sign-in/location-selection-modal.component";
import { LocationItem, LocationService } from "../core/services/location.service";
import { FormToasterService, ToastMessage } from "../core/services/form-toaster.service";
import { NiToasterComponent } from "../shared/lib/form/ni-toaster/ni-toaster.component";
import { Subscription } from "rxjs";

@Component({
  selector: "staff-delivery-layout-component, StaffDeliveryLayoutComponent",
  standalone: true,
  templateUrl: './staff-delivery-layout.component.html',
  styleUrls: ['./staff-delivery-layout.component.css'],
  imports: [CommonModule, LibraryModule, RouterOutlet, RouterModule, LocationSelectionModalComponent, NiToasterComponent]
})
export class StaffDeliveryLayoutComponent implements OnDestroy {
  @ViewChild('profileDropdown') profileDropdown!: ElementRef;
  @ViewChild('profileContainer') profileContainer!: ElementRef;
  
  showProfileMenu = false;
  showLocationSwitcher = false;
  switchLocations: LocationItem[] = [];
  isRefreshingLocations = false;
  toasts: ToastMessage[] = [];
  private locationDisplayTick = 0;
  private locationRefreshSubscription?: Subscription;
  private toasterSubscription?: Subscription;
  
  breadcrumbList = [
    { label: '', routerLink: '/', icon: 'home_04' },
    { label: 'Meal', routerLink: '/meal-delivery' },
    { label: 'Lunch', routerLink: '/lunch' }
  ];
  staffInfo: any = getStaffInfo();

  private readonly router = inject(Router);
  private readonly staffLogoutService = inject(StaffLogoutService);
  private readonly locationService = inject(LocationService);
  readonly toasterService = inject(FormToasterService);

  constructor() {
    this.toasterSubscription = this.toasterService.toasts$.subscribe((toasts) => {
      this.toasts = toasts;
    });
  }

  get selectedLocationName(): string {
    void this.locationDisplayTick;
    return getSelectedLocation()?.locationName ?? '';
  }

  get selectedLocationId(): string {
    void this.locationDisplayTick;
    return getSelectedLocation()?.id ?? '';
  }

  toggleProfileMenu(event: Event): void {
    event.stopPropagation();
    this.showProfileMenu = !this.showProfileMenu;
  }

  onLogout(): void {
    this.showProfileMenu = false;
    this.staffLogoutService.requestLogout();
  }

  openLocationSwitcher(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.selectedLocationName || this.isRefreshingLocations) {
      return;
    }

    this.switchLocations = getStaffAssignedLocations();
    this.showLocationSwitcher = true;
    this.isRefreshingLocations = true;
    this.locationRefreshSubscription?.unsubscribe();
    this.locationRefreshSubscription = this.locationService.getMyAssignedLocations().subscribe({
      next: (response) => {
        this.isRefreshingLocations = false;
        const locations = response.locations ?? [];
        if (locations.length > 0) {
          setStaffAssignedLocations(locations);
          this.switchLocations = locations;
          this.locationDisplayTick += 1;
        }
        if (locations.length <= 1) {
          this.toasterService.showError(
            locations.length === 0
              ? 'No centers are assigned to your account.'
              : 'Only one center is assigned. Ask an admin to assign another center to switch.',
            'top-right',
            5000,
          );
          if (locations.length === 0) {
            this.showLocationSwitcher = false;
          }
        }
      },
      error: () => {
        this.isRefreshingLocations = false;
        if (this.switchLocations.length <= 1) {
          this.toasterService.showError(
            'Unable to load your assigned centers. Please try again.',
            'top-right',
            5000,
          );
          this.showLocationSwitcher = false;
        }
      },
    });
  }

  onLocationSwitchConfirmed(location: LocationItem): void {
    const previousId = getSelectedLocation()?.id ?? '';
    this.showLocationSwitcher = false;
    if (!location?.id || location.id === previousId) {
      return;
    }
    setSelectedLocation({
      id: location.id,
      locationName: location.locationName,
      addressLine: location.addressLine,
    });
    this.locationDisplayTick += 1;
    this.toasterService.showSuccess(
      `Working location changed to ${location.locationName}.`,
      'top-right',
      5000,
    );
    const url = this.router.url;
    void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      void this.router.navigateByUrl(url);
    });
  }

  onLocationSwitchCancelled(): void {
    this.showLocationSwitcher = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.profileContainer?.nativeElement.contains(event.target)) {
      return;
    }
    if (this.profileDropdown?.nativeElement.contains(event.target)) {
      return;
    }
    this.showProfileMenu = false;
  }

  formatTime(time: string): string {
    return formatTime(time);
  }

  navigateToPrograms(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    // Exit to Programs home (not Senior Program deep link).
    void this.router.navigate(['/programs']);
  }

  ngOnDestroy(): void {
    this.locationRefreshSubscription?.unsubscribe();
    this.toasterSubscription?.unsubscribe();
  }
}
